import { ControlsLessonId } from "game-common/models"
import { Protocol } from "game-common/protocol"
import { clamp01, easeInCubic, easeOutBack, easeOutCubic } from "./chapter_reward_motion"

/**
 * The story's controls lessons: floating notes that teach a control at the moment the player
 * first needs it (gui/controls_lesson.ts draws them). Each lesson is its notes in turn:
 *
 *   move      MOVE, on the wreck beach (chapter 0) the moment the player is on their feet; it goes
 *             1.5s after they first move. It never goes by itself.
 *   attack    ATTACK, a moment after chapter one's camp appears; it goes once they have swung (or
 *             after a while).
 *   ready     The ready bar's keys (on a phone, a tap on a slot), once the Lord has handed over his
 *             axe. A slot's key toggles its item, so the first press usually puts the axe AWAY: the
 *             note answers that by asking for it back, and waits for it however long that takes.
 *             It goes once a press leaves something in hand (or after a while, if nothing is pressed).
 *   interact  INTERACT, beside the cold campfire with wood in the pack; it goes once they have
 *             interacted with something (or after a while).
 *
 * The server says which lesson is wanted (a "hud" screen message: chapter_hud.ts), and ends one
 * early by naming none -- reaching the Lord ends the attack lesson, walking off from the fire ends
 * the interact lesson. Everything else is the player's own input, so it runs here: the clock and the
 * rules, as pure functions of elapsed time like the clue callouts and the checklist rewards, so a
 * test can pose any instant without a renderer. A lesson seen through to the end is told back to the
 * server (takeFinished): the beach's move note going is the castaway's cue to speak (ember-tutorial.js).
 *
 * The clock only runs while the world is in view. Under a title card, the curtain over a loading
 * room or a screen that covers the world, the notes fade away and wait, and come back where they
 * left off.
 */
export const CONTROLS_LESSON = {
    /**
     * From a lesson being asked for (and the world being in view) to its first note starting in, so a
     * note never lands on a world that has only just appeared. Not for MOVE: it is asked for once the
     * player has got up off the beach's sand, with the world long in view, and comes in at once.
     */
    FIRST_NOTE_DELAY_MS: 700,
    IN_MS: 520,
    OUT_MS: 420,
    /** The movement note goes this long after the player first moves. */
    AFTER_FIRST_STEP_MS: 1500,
    /** Between one note finishing its exit and the next starting in. */
    HANDOVER_GAP_MS: 250,
    /** An action's note goes this long after the action was used, so its tick can land. */
    USED_HOLD_MS: 900,
    /** ...or this long after it came, used or not. */
    ACTION_MAX_MS: 15000,
    /** Fading out under something that covers the world, and back in after it. */
    PRESENCE_IN_MS: 260,
    PRESENCE_OUT_MS: 170,
    /** A frame longer than this (a hitch, a backgrounded tab) advances the lesson by only this much. */
    MAX_STEP_MS: 100,
} as const

export type ControlsStep = "move" | "attack" | "ready" | "interact"
export type ControlsAction = "attack" | "interact"
/** What a press on a ready-bar slot did to the player's hand: took the item out, or put it away. */
export type ReadyPress = "arm" | "stow"

export const CONTROLS_LESSON_STEPS: Record<ControlsLessonId, ControlsStep[]> = {
    move: ["move"],
    attack: ["attack"],
    ready: ["ready"],
    interact: ["interact"],
}

/** How long a lesson's first note waits for the world to have been in view (see FIRST_NOTE_DELAY_MS). */
export const firstNoteDelayMs = (lesson: ControlsLessonId): number =>
    lesson === "move" ? 0 : CONTROLS_LESSON.FIRST_NOTE_DELAY_MS

export interface ControlsMoment {
    /** Something covers the world: a title card, a loading room's curtain, a world-blocking screen. */
    blocked: boolean
    /** The player is walking, by keys or stick. */
    moving: boolean
    attacking: boolean
    interacting: boolean
    /** A ready-bar slot was pressed this frame, and what it did. */
    readyPress?: ReadyPress
}

export interface ControlsNotePose {
    alpha: number
    /** Screen units down from the resting place: up through the entrance, then lifting away. */
    rise: number
    scale: number
    /** 0..1..0 as the note goes: its labels warm to gold. */
    flash: number
    /** Lesson-clock ms since the note started in. */
    age: number
}

interface StepWindow {
    step: ControlsStep
    inAt?: number
    outAt?: number
    usedAt?: number
    /** The ready note: when a press last put the item in hand away. */
    stowedAt?: number
}

export class ControlsLessonState {
    private clock = 0
    private presence = 0
    private lesson?: ControlsLessonId
    private next?: ControlsLessonId
    private steps: StepWindow[] = []
    private firstStepAt?: number
    private dismissedAt?: number
    // Lessons the player has been through to the end in this scene. One the server cut short (they
    // walked off) is not among them, and is given again when it is asked for again.
    private completed = new Set<ControlsLessonId>()
    // Lessons seen through to the end that nobody has asked about yet (see takeFinished).
    private finished: ControlsLessonId[] = []

    /**
     * The server asks for a lesson. It says so again on every change to the HUD it holds back, so a
     * lesson already under way, or already finished in this scene, is not started over. Asked for
     * while a different one is up, that one goes first.
     */
    request = (lesson: ControlsLessonId): boolean => {
        // A lesson this client has no notes for -- one added on a newer server -- is not taught.
        if (!CONTROLS_LESSON_STEPS[lesson]) return false
        this.settle()
        if (this.completed.has(lesson) || this.lesson === lesson) return false
        if (this.lesson !== undefined) {
            this.next = lesson
            this.dismiss()
            return true
        }
        this.start(lesson)
        return true
    }

    /**
     * The server wants no lesson now: the note on screen fades out as if its time had come, and
     * nothing comes after it. A no-op with nothing under way.
     */
    dismiss = () => {
        this.settle()
        if (this.lesson === undefined || this.dismissedAt !== undefined) return
        this.dismissedAt = this.clock
        for (const window of this.steps) {
            if (window.inAt === undefined || this.clock < window.inAt) {
                // Not on screen yet, and now never will be.
                window.inAt = undefined
            } else if (window.outAt === undefined || window.outAt > this.clock) {
                window.outAt = this.clock
            }
        }
    }

    /** A new scene: forget everything, including which lessons were given. */
    reset = () => {
        this.lesson = this.next = undefined
        this.steps = []
        this.completed.clear()
        this.finished = []
        this.restart()
    }

    isActive = (): boolean => {
        this.settle()
        return this.lesson !== undefined
    }

    /** The lesson on screen (or waiting to come on), if any. */
    current = (): ControlsLessonId | undefined => {
        this.settle()
        return this.lesson
    }

    hasCompleted = (lesson: ControlsLessonId): boolean => this.completed.has(lesson)

    /** The lessons the player has seen through to the end since this was last asked, oldest first. */
    takeFinished = (): ControlsLessonId[] => {
        this.settle()
        const finished = this.finished
        this.finished = []
        return finished
    }

    now = (): number => this.clock

    /** When the player first moved in this lesson, on the lesson clock. */
    firstStep = (): number | undefined => this.firstStepAt

    /** When an action's note saw it used, on the lesson clock. For the ready note: a press that took something out. */
    usedAt = (step: ControlsAction | "ready"): number | undefined => this.window(step)?.usedAt

    /** When the ready note saw the item in hand put away (and is now asking for it back), if it has. */
    stowedAt = (): number | undefined => this.window("ready")?.stowedAt

    update = (deltaMs: number, moment: ControlsMoment) => {
        if (!this.isActive()) return
        const step = Math.max(0, Math.min(deltaMs, CONTROLS_LESSON.MAX_STEP_MS))
        this.presence = moment.blocked
            ? Math.max(0, this.presence - step / CONTROLS_LESSON.PRESENCE_OUT_MS)
            : Math.min(1, this.presence + step / CONTROLS_LESSON.PRESENCE_IN_MS)
        if (moment.blocked) return
        // Dismissed: only the exit is left to play.
        if (this.dismissedAt !== undefined) {
            this.clock += step
            return
        }
        // The first frame the world can be seen starts the count to the first note.
        if (this.steps[0].inAt === undefined) this.steps[0].inAt = this.clock + firstNoteDelayMs(this.lesson!)
        this.clock += step

        this.steps.forEach((window, i) => {
            if (window.inAt === undefined || window.outAt !== undefined) return
            if (window.step === "move") {
                // A step taken before the note has had a moment on screen still leaves it time to be read.
                if (moment.moving && this.firstStepAt === undefined) {
                    this.firstStepAt = Math.max(this.clock, window.inAt + CONTROLS_LESSON.IN_MS * 0.6)
                }
                if (this.firstStepAt !== undefined) window.outAt = this.firstStepAt + CONTROLS_LESSON.AFTER_FIRST_STEP_MS
            } else if (window.step === "ready" && this.clock >= window.inAt) {
                // Only a press made while the note is up counts. Putting the item away is not the end
                // of the lesson but its middle: the note asks for it back and, having asked, waits.
                if (window.usedAt === undefined && moment.readyPress === "stow") window.stowedAt = this.clock
                if (window.usedAt === undefined && moment.readyPress === "arm") window.usedAt = this.clock
                if (window.usedAt !== undefined) window.outAt = window.usedAt + CONTROLS_LESSON.USED_HOLD_MS
                else if (window.stowedAt === undefined && this.clock >= window.inAt + CONTROLS_LESSON.ACTION_MAX_MS) window.outAt = this.clock
            } else if (this.clock >= window.inAt) {
                // Only what is done while the note is up counts toward it.
                const used = window.step === "attack" ? moment.attacking : moment.interacting
                if (used && window.usedAt === undefined) window.usedAt = this.clock
                if (window.usedAt !== undefined) window.outAt = window.usedAt + CONTROLS_LESSON.USED_HOLD_MS
                else if (this.clock >= window.inAt + CONTROLS_LESSON.ACTION_MAX_MS) window.outAt = this.clock
            }
            const following = this.steps[i + 1]
            if (window.outAt !== undefined && following && following.inAt === undefined) {
                following.inAt = window.outAt + CONTROLS_LESSON.OUT_MS + CONTROLS_LESSON.HANDOVER_GAP_MS
            }
        })
    }

    /** Where a note is in its life right now, or null while it is not on screen at all. */
    pose = (step: ControlsStep): ControlsNotePose | null => {
        const window = this.window(step)
        if (!window || window.inAt === undefined || this.clock < window.inAt) return null
        const pin = clamp01((this.clock - window.inAt) / CONTROLS_LESSON.IN_MS)
        const pout = window.outAt === undefined ? 0 : clamp01((this.clock - window.outAt) / CONTROLS_LESSON.OUT_MS)
        if (pout >= 1) return null
        const ein = easeOutCubic(pin), eout = easeInCubic(pout)
        return {
            alpha: ein * (1 - eout) * this.presence,
            rise: (1 - ein) * 8 - (window.outAt === undefined ? 0 : easeOutCubic(pout) * 6),
            scale: 0.94 + 0.06 * easeOutBack(pin, 1.5) - 0.03 * eout,
            flash: window.outAt !== undefined && pout < 0.5 ? Math.sin((Math.PI * pout) / 0.5) : 0,
            age: this.clock - window.inAt,
        }
    }

    private window = (step: ControlsStep) => this.steps.find(window => window.step === step)

    private start = (lesson: ControlsLessonId) => {
        this.lesson = lesson
        this.steps = CONTROLS_LESSON_STEPS[lesson].map(step => ({ step }))
        this.restart()
    }

    private restart = () => {
        this.clock = 0
        this.presence = 0
        this.firstStepAt = undefined
        this.dismissedAt = undefined
    }

    // Has the lesson on screen played out? Then put it away -- as completed, if the player saw it
    // through rather than the server ending it -- and start whatever was asked for meanwhile.
    private settle = () => {
        if (this.lesson === undefined) return
        const last = this.steps[this.steps.length - 1]
        const over = (this.dismissedAt !== undefined && this.clock >= this.dismissedAt + CONTROLS_LESSON.OUT_MS) ||
            (last.outAt !== undefined && this.clock >= last.outAt + CONTROLS_LESSON.OUT_MS)
        if (!over) return
        if (this.dismissedAt === undefined) {
            this.completed.add(this.lesson)
            this.finished.push(this.lesson)
        }
        const next = this.next
        this.lesson = this.next = undefined
        this.steps = []
        if (next && !this.completed.has(next)) this.start(next)
    }
}

/**
 * Which action, if any, a message the player's client sent counts as. Read off what was SENT, so a
 * session replay (which has the player's sends and no mouse) counts exactly what the player's own
 * screen counted. A client also answers an npc's swing with a startAttack of its own: only the
 * player's counts. A held interaction refreshes and cancels as it goes: only its start counts.
 * A ready-bar slot's key (or a tap on it) toggles its item, and the toggle is in what was sent:
 * "activate" took the item out, "deactivate" put it away.
 */
export const controlsActionSent = (payloadType: string, payload: any, playerId: string | undefined): ControlsAction | ReadyPress | undefined => {
    if (payloadType === Protocol.type_inventoryAction) {
        if (!playerId || payload?.entityId !== playerId) return undefined
        return payload.action === "activate" ? "arm" : payload.action === "deactivate" ? "stow" : undefined
    }
    if (payloadType === Protocol.type_startAttack) return playerId && payload?.entityId === playerId ? "attack" : undefined
    if (payloadType === Protocol.type_playAttackAnimation) return "attack"
    if (payloadType === Protocol.type_playerEvent && payload?.playerEventType === "ACTIVATE_ENTITY_INTERACTION") {
        const phase = payload.context?.holdPhase
        return phase === undefined || phase === "begin" ? "interact" : undefined
    }
    return undefined
}
